<template>
  <div class="report-page student-balance-report">
    <ReportViewBar title="Student Balances by Department" />

    <v-container fluid>
      <div class="student-balance-report__heading">
        <div>
          <div class="student-balance-report__eyebrow">Billing report</div>
          <h1>Students with outstanding balances</h1>
          <p>Students are grouped by department. Only balances greater than zero are included.</p>
        </div>
        <div class="student-balance-report__actions">
          <v-btn color="primary" depressed :disabled="isLoading || !groups.length" @click="printPdf">
            <v-icon left small>mdi-printer</v-icon>Print PDF
          </v-btn>
          <v-btn color="primary" outlined :loading="isLoading" @click="loadData">
            <v-icon left small>mdi-refresh</v-icon>Refresh
          </v-btn>
        </div>
      </div>

      <v-row class="student-balance-report__metrics">
        <v-col cols="12" sm="4"><div class="student-balance-report__metric"><span>Departments</span><strong>{{ groups.length }}</strong></div></v-col>
        <v-col cols="12" sm="4"><div class="student-balance-report__metric"><span>Students with balance</span><strong>{{ studentCount }}</strong></div></v-col>
        <v-col cols="12" sm="4"><div class="student-balance-report__metric"><span>Total outstanding</span><strong class="error--text">{{ grandTotal | currency }}</strong></div></v-col>
      </v-row>

      <v-card class="student-balance-report__card" elevation="0">
        <div class="student-balance-report__toolbar">
          <v-text-field v-model="search" outlined dense clearable hide-details prepend-inner-icon="mdi-magnify" placeholder="Search student or department" />
          <v-autocomplete
            v-model="selectedDepartmentIds"
            :items="departmentOptions"
            item-text="name"
            item-value="id"
            multiple
            clearable
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-school-outline"
            label="Departments"
            placeholder="All departments"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index < 2" small close @click:close="removeDepartment(item.id)">{{ item.name }}</v-chip>
              <span v-if="index === 2" class="student-balance-report__more">+{{ selectedDepartmentIds.length - 2 }} more</span>
            </template>
          </v-autocomplete>
        </div>
        <div v-if="!selectedDepartmentIds.length" class="student-balance-report__all-label">Showing all departments</div>

        <v-progress-linear v-if="isLoading" indeterminate color="primary" />

        <v-alert v-else-if="error" type="error" text class="ma-5">{{ error }}</v-alert>

        <div v-else-if="groups.length || visibleUnassigned.length" class="student-balance-report__groups">
          <section v-for="group in groups" :key="group.id" class="student-balance-report__group">
            <header>
              <div><v-icon color="primary" size="20">mdi-school-outline</v-icon><div><h2>{{ group.name }}</h2><span>{{ group.students.length }} {{ group.students.length === 1 ? "student" : "students" }}</span></div></div>
              <strong>{{ group.total | currency }}</strong>
            </header>

            <v-simple-table dense>
              <template v-slot:default>
                <thead><tr><th>Student name</th><th>Father / guardian</th><th>Phone number</th><th class="text-right">Outstanding balance</th></tr></thead>
                <tbody>
                  <tr
                    v-for="student in group.students"
                    :key="student.studentId"
                    class="student-balance-report__student-row"
                    tabindex="0"
                    title="Open parent profile"
                    @click="openParentProfile(student)"
                    @keydown.enter="openParentProfile(student)"
                  >
                    <td><v-icon class="mr-2" small color="primary">mdi-account-arrow-right-outline</v-icon>{{ studentName(student) }}</td>
                    <td>{{ student.fatherName || student.parentName || "—" }}</td>
                    <td>
                      <a v-if="student.phoneNumber" class="student-balance-report__phone" :href="`tel:${student.phoneNumber}`" @click.stop>{{ student.phoneNumber }}</a>
                      <span v-else>—</span>
                    </td>
                    <td class="text-right student-balance-report__balance">{{ Number(student.balance || 0) | currency }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </section>

          <section v-if="visibleUnassigned.length" class="student-balance-report__group student-balance-report__group--unassigned">
            <header>
              <div><v-icon color="warning darken-2" size="20">mdi-account-question-outline</v-icon><div><h2>Unassigned parent accounts</h2><span>These balances need a student or department assignment</span></div></div>
              <strong>{{ unassignedVisibleTotal | currency }}</strong>
            </header>
            <v-simple-table dense>
              <template v-slot:default>
                <thead><tr><th>Parent account</th><th>Reason</th><th class="text-right">Outstanding balance</th></tr></thead>
                <tbody>
                  <tr v-for="account in visibleUnassigned" :key="account.customerId" class="student-balance-report__student-row" tabindex="0" title="Open parent profile" @click="openParentProfile(account)" @keydown.enter="openParentProfile(account)">
                    <td><v-icon class="mr-2" small color="warning darken-2">mdi-account-arrow-right-outline</v-icon>{{ account.parentName }}</td>
                    <td class="student-balance-report__reason">{{ account.reason }}</td>
                    <td class="text-right student-balance-report__balance">{{ Number(account.balance || 0) | currency }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </section>
        </div>

        <div v-else class="student-balance-report__empty">
          <v-icon size="52" color="green lighten-2">mdi-check-circle-outline</v-icon>
          <strong>{{ search ? "No matching balances" : "No outstanding balances" }}</strong>
          <span>{{ search ? "Try a different student, parent, or department name." : "Every parent account is currently clear." }}</span>
        </div>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import ReportViewBar from "@/components/ReportViewBar";
import reportService from "@/modules/ReportView/service.js";
import pdfMake from "pdfmake/build/pdfmake.min";
import { pdfMake as pdfFonts } from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.vfs;

export default {
  name: "student-balances-by-department",
  components: { ReportViewBar },
  data() {
    return { isLoading: true, rows: [], unassigned: [], reportTotal: 0, reconciled: true, search: "", selectedDepartmentIds: [], error: null };
  },
  computed: {
    departmentOptions() {
      const departments = new Map();
      this.rows.forEach((row) => departments.set(String(row.departmentId), row.departmentName || "No department"));
      const options = Array.from(departments, ([id, name]) => ({ id, name }));
      if (this.unassigned.length) options.push({ id: "__unassigned__", name: "Unassigned parent accounts" });
      return options;
    },
    filteredRows() {
      const search = String(this.search || "").trim().toLowerCase();
      const selected = new Set(this.selectedDepartmentIds.map(String).filter((id) => id !== "__unassigned__"));
      const hasDepartmentSelection = this.selectedDepartmentIds.some((id) => String(id) !== "__unassigned__");
      return this.rows.filter((row) => {
        const departmentMatches = !hasDepartmentSelection ? !this.selectedDepartmentIds.includes("__unassigned__") : selected.has(String(row.departmentId));
        const searchMatches = !search || `${row.departmentName || ""} ${this.studentName(row)}`.toLowerCase().includes(search);
        return departmentMatches && searchMatches;
      });
    },
    groups() {
      const groups = new Map();
      this.filteredRows.forEach((row) => {
        const key = String(row.departmentId);
        if (!groups.has(key)) groups.set(key, { id: key, name: row.departmentName || "No department", students: [], total: 0 });
        const group = groups.get(key);
        group.students.push(row);
        group.total += Number(row.balance || 0);
      });
      return Array.from(groups.values());
    },
    visibleUnassigned() {
      const showUnassigned = !this.selectedDepartmentIds.length || this.selectedDepartmentIds.map(String).includes("__unassigned__");
      if (!showUnassigned) return [];
      const search = String(this.search || "").trim().toLowerCase();
      if (!search) return this.unassigned;
      return this.unassigned.filter((row) => `${row.parentName || ""} ${row.reason || ""} unassigned`.toLowerCase().includes(search));
    },
    studentCount() {
      return new Set(this.filteredRows.map((row) => String(row.studentId))).size;
    },
    unassignedVisibleTotal() {
      return this.visibleUnassigned.reduce((sum, row) => sum + Number(row.balance || 0), 0);
    },
    grandTotal() {
      return this.filteredRows.reduce((sum, row) => sum + Number(row.balance || 0), 0) + this.unassignedVisibleTotal;
    },
  },
  created() { this.loadData(); },
  methods: {
    studentName(student) { return [student.firstName, student.lastName].filter(Boolean).join(" ") || "Unnamed student"; },
    openParentProfile(student) {
      const customerId = student.customerId || student.CustomerId;
      if (!customerId) return;
      this.$router.push({ path: `/parents/${customerId}/profile` });
    },
    removeDepartment(id) {
      this.selectedDepartmentIds = this.selectedDepartmentIds.filter((selectedId) => String(selectedId) !== String(id));
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(value || 0));
    },
    printPdf() {
      const content = [
        { text: "Students with Outstanding Balances", style: "title" },
        { text: this.selectedDepartmentIds.length ? `${this.groups.length} selected department${this.groups.length === 1 ? "" : "s"}` : "All departments", style: "subtitle" },
        { text: `Generated ${new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(new Date())}`, style: "generated" },
      ];

      this.groups.forEach((group) => {
        const body = [
          [
            { text: group.name, style: "department", colSpan: 4 },
            {}, {}, {},
          ],
          [
            { text: "Student name", style: "tableHeader" },
            { text: "Father / guardian", style: "tableHeader" },
            { text: "Phone number", style: "tableHeader" },
            { text: "Outstanding balance", style: "tableHeader", alignment: "right" },
          ],
          ...group.students.map((student) => [
            { text: this.studentName(student), style: "student" },
            { text: student.fatherName || student.parentName || "—", style: "student" },
            { text: student.phoneNumber || "—", style: "student" },
            { text: this.formatCurrency(student.balance), style: "balance", alignment: "right" },
          ]),
          [
            { text: `${group.name} subtotal`, style: "subtotal", colSpan: 3 }, {}, {},
            { text: this.formatCurrency(group.total), style: "subtotal", alignment: "right" },
          ],
        ];
        content.push({ table: { headerRows: 2, widths: ["*", 125, 95, 90], body }, layout: "lightHorizontalLines", margin: [0, 14, 0, 0] });
      });

      if (this.visibleUnassigned.length) {
        content.push({
          table: {
            headerRows: 2,
            widths: [150, "*", 100],
            body: [
              [{ text: "Unassigned parent accounts", style: "unassigned", colSpan: 3 }, {}, {}],
              [{ text: "Parent account", style: "tableHeader" }, { text: "Reason", style: "tableHeader" }, { text: "Balance", style: "tableHeader", alignment: "right" }],
              ...this.visibleUnassigned.map((account) => [
                { text: account.parentName || "Parent account", style: "student" },
                { text: account.reason || "Not assigned", style: "student" },
                { text: this.formatCurrency(account.balance), style: "balance", alignment: "right" },
              ]),
            ],
          },
          layout: "lightHorizontalLines",
          margin: [0, 14, 0, 0],
        });
      }

      content.push({
        columns: [
          { text: `Grand total · ${this.studentCount} student${this.studentCount === 1 ? "" : "s"}${this.visibleUnassigned.length ? ` · ${this.visibleUnassigned.length} unassigned` : ""}`, style: "grandTotal" },
          { text: this.formatCurrency(this.grandTotal), style: "grandTotal", alignment: "right" },
        ],
        margin: [0, 18, 0, 0],
      });

      pdfMake.createPdf({
        pageSize: "LETTER",
        pageOrientation: "portrait",
        pageMargins: [42, 42, 42, 42],
        content,
        styles: {
          title: { fontSize: 19, bold: true, color: "#25342b" },
          subtitle: { fontSize: 10, color: "#52705d", margin: [0, 4, 0, 0] },
          generated: { fontSize: 8, color: "#7b887f", margin: [0, 3, 0, 8] },
          department: { fontSize: 11, bold: true, color: "#245c37", fillColor: "#edf5ef", margin: [4, 4, 4, 4] },
          unassigned: { fontSize: 11, bold: true, color: "#7a5412", fillColor: "#fff6df", margin: [4, 4, 4, 4] },
          tableHeader: { fontSize: 9, bold: true, color: "#48564d", fillColor: "#f6f8f6", margin: [4, 3, 4, 3] },
          student: { fontSize: 9, margin: [4, 3, 4, 3] },
          balance: { fontSize: 9, bold: true, color: "#b3261e", margin: [4, 3, 4, 3] },
          subtotal: { fontSize: 9, bold: true, fillColor: "#fafafa", margin: [4, 3, 4, 3] },
          grandTotal: { fontSize: 11, bold: true, color: "#25342b" },
        },
        defaultStyle: { fontSize: 9 },
        footer(currentPage, pageCount) {
          return { text: `Page ${currentPage} of ${pageCount}`, alignment: "center", color: "#888888", fontSize: 8, margin: [0, 12, 0, 0] };
        },
      }).print();
    },
    async loadData() {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await reportService.studentBalancesByDepartment();
        const report = response.data || {};
        this.rows = (report.rows || []).filter((row) => Number(row.balance || 0) > 0);
        this.unassigned = (report.unassigned || []).filter((row) => Number(row.balance || 0) > 0);
        this.reportTotal = Number(report.totalOutstanding || 0);
        this.reconciled = report.reconciled !== false;
      } catch (error) {
        this.error = error?.response?.data?.message || error?.data?.message || error.message || "Unable to load student balances.";
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.student-balance-report { min-height: 100%; background: #f7f9f7; }
.student-balance-report__heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; padding: 8px 0 18px; }
.student-balance-report__eyebrow { color: var(--v-primary-base); font-size: 11px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; }
.student-balance-report__heading h1 { margin: 4px 0; color: #25342b; font-size: 27px; }
.student-balance-report__heading p { margin: 0; color: #738078; font-size: 13px; }
.student-balance-report__actions { display: flex; gap: 10px; }
.student-balance-report__metrics { margin-bottom: 6px; }
.student-balance-report__metric { display: flex; min-height: 76px; padding: 16px 18px; border: 1px solid #e3e9e5; border-radius: 12px; background: #fff; flex-direction: column; }
.student-balance-report__metric span { color: #7b887f; font-size: 11px; }
.student-balance-report__metric strong { margin-top: 4px; color: #2d4034; font-size: 22px; }
.student-balance-report__card { overflow: hidden; border: 1px solid #e1e7e3; border-radius: 14px; }
.student-balance-report__toolbar { display: grid; padding: 16px 16px 5px; gap: 12px; grid-template-columns: minmax(240px, 1fr) minmax(280px, 1fr); }
.student-balance-report__all-label { padding: 0 18px 12px; color: #7b887f; font-size: 10px; }
.student-balance-report__more { color: #66736a; font-size: 11px; }
.student-balance-report__groups { padding: 0 16px 18px; }
.student-balance-report__group { overflow: hidden; margin-top: 14px; border: 1px solid #e3e9e5; border-radius: 11px; }
.student-balance-report__group header { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 13px 16px; background: #f4f8f5; }
.student-balance-report__group header > div { display: flex; align-items: center; gap: 10px; }
.student-balance-report__group h2 { margin: 0; color: #314239; font-size: 14px; }
.student-balance-report__group header span { display: block; margin-top: 1px; color: #849087; font-size: 10px; }
.student-balance-report__group header > strong { color: #c62828; font-size: 14px; }
.student-balance-report__group--unassigned { border-color: #ead8ae; }
.student-balance-report__group--unassigned header { background: #fffaf0; }
.student-balance-report__balance { color: #c62828; font-weight: 700; }
.student-balance-report__phone { color: var(--v-primary-base); font-weight: 600; text-decoration: none; }
.student-balance-report__phone:hover { text-decoration: underline; }
.student-balance-report__reason { color: #7b887f; font-size: 11px; }
.student-balance-report__student-row { cursor: pointer; }
.student-balance-report__student-row:hover, .student-balance-report__student-row:focus { background: #f5faf6; outline: none; }
.student-balance-report__empty { display: flex; min-height: 280px; align-items: center; justify-content: center; color: #758279; flex-direction: column; text-align: center; }
.student-balance-report__empty strong { margin-top: 12px; color: #405047; font-size: 16px; }
.student-balance-report__empty span { margin-top: 4px; font-size: 12px; }
@media (max-width: 599px) { .student-balance-report__heading { flex-direction: column; } .student-balance-report__heading h1 { font-size: 22px; } .student-balance-report__actions { width: 100%; flex-wrap: wrap; } .student-balance-report__toolbar { grid-template-columns: 1fr; } }
@media print { .student-balance-report { background: #fff; } .student-balance-report__toolbar, .student-balance-report__heading .v-btn { display: none; } .student-balance-report__card, .student-balance-report__group { border-color: #bbb; } }
</style>
